import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContentExtrasBar } from '@/components/content/ContentExtrasBar';
import { getVideoPosition, saveVideoPosition } from '@/hooks/useVimeo';
import type { ContentProgressUpdate } from '@/components/ContentPlayerModal';
import Player from '@vimeo/player';

const THROTTLE_MS = 10_000;

interface VimeoPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  vimeoPlayerUrl: string;
  title: string;
  onComplete?: (contentId: string) => void;
  onProgressSaved?: (progress: ContentProgressUpdate) => void;
  isCompleted?: boolean;
  contentDescription?: string | null;
}

/** Build the iframe src from the vimeoPlayerUrl, adding embed params */
function buildIframeSrc(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set('autoplay', '1');
    u.searchParams.set('byline', '0');
    u.searchParams.set('title', '0');
    u.searchParams.set('portrait', '0');
    u.searchParams.set('dnt', '1');
    return u.toString();
  } catch {
    // fallback: just append params
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}autoplay=1&byline=0&title=0&portrait=0&dnt=1`;
  }
}

export function VimeoPlayerModal({
  isOpen,
  contentDescription,
  onClose,
  contentId,
  vimeoPlayerUrl,
  title,
  onComplete,
  onProgressSaved,
  isCompleted: initialCompleted = false,
}: VimeoPlayerModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  const lastSaveTs = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(initialCompleted);

  const notify = useCallback(
    (pct: number, done: boolean) => {
      onProgressSaved?.({ contentId, completed: done, progressPercent: Math.round(pct) });
    },
    [contentId, onProgressSaved],
  );

  const persist = useCallback(
    async (pos: number, pct: number) => {
      try {
        const saved = await saveVideoPosition(contentId, pos, pct);
        if (saved) {
          setCompleted(saved.completed);
          notify(saved.progress_percent, saved.completed);
        }
        return saved;
      } catch (e) {
        console.error('[VimeoPlayer] persist error', e);
        return null;
      }
    },
    [contentId, notify],
  );

  const persistCurrent = useCallback(
    async (player: Player) => {
      try {
        const [t, d] = await Promise.all([player.getCurrentTime(), player.getDuration()]);
        return persist(t, d > 0 ? (t / d) * 100 : 0);
      } catch {
        return null;
      }
    },
    [persist],
  );

  useEffect(() => {
    setCompleted(initialCompleted);
  }, [contentId, initialCompleted]);

  // Attach SDK to the iframe once it loads
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const attachPlayer = async () => {
      const iframe = iframeRef.current;
      if (!iframe || cancelled) return;

      try {
        const player = new Player(iframe);
        playerRef.current = player;

        await player.ready();
        if (cancelled) return;

        // Resume position
        const saved = await getVideoPosition(contentId);
        if (cancelled) return;
        if (saved?.completed) setCompleted(true);
        if (saved && saved.last_position > 0 && !saved.completed) {
          try { await player.setCurrentTime(saved.last_position); } catch { /* ignore */ }
        }

        // Events
        player.on('play', () => {
          if (!cancelled) void persistCurrent(player);
        });

        player.on('timeupdate', (e: { seconds?: number; duration?: number }) => {
          if (cancelled) return;
          const now = Date.now();
          if (now - lastSaveTs.current < THROTTLE_MS) return;
          lastSaveTs.current = now;
          const s = Number(e.seconds ?? 0);
          const d = Number(e.duration ?? 0);
          void persist(s, d > 0 ? (s / d) * 100 : 0);
        });

        player.on('pause', () => {
          if (!cancelled) void persistCurrent(player);
        });

        player.on('ended', () => {
          if (cancelled) return;
          void persist(0, 100).then(() => {
            if (!cancelled) {
              setCompleted(true);
              onComplete?.(contentId);
            }
          });
        });
      } catch (e: any) {
        console.error('[VimeoPlayer] SDK attach error', e);
      }
    };

    // Small delay to ensure iframe is in DOM after dialog portal mounts
    const timer = setTimeout(attachPlayer, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      const p = playerRef.current;
      playerRef.current = null;
      if (p) {
        void persistCurrent(p)
          .catch(() => undefined)
          .finally(() => { p.destroy().catch(() => undefined); });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contentId, vimeoPlayerUrl]);

  const handleClose = useCallback(() => {
    const p = playerRef.current;
    playerRef.current = null;

    if (p) {
      void persistCurrent(p)
        .catch(() => undefined)
        .finally(() => {
          p.destroy().catch(() => undefined);
          onClose();
        });
    } else {
      onClose();
    }
  }, [onClose, persistCurrent]);

  const iframeSrc = isOpen ? buildIframeSrc(vimeoPlayerUrl) : '';

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => { if (!open) handleClose(); }}
    >
      <DialogContent className="max-w-5xl w-full p-0 border-none overflow-hidden bg-sidebar [&>button]:hidden">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          Reproductor de video del contenido {title}.
        </DialogDescription>
        {contentId && <ContentExtrasBar contentId={contentId} description={contentDescription} />}

        <div className="relative aspect-video bg-sidebar">
          {/* Declarative iframe — always mounts reliably */}
          {isOpen && (
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              className="absolute inset-0 h-full w-full border-0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setError('No se pudo cargar el video.');
              }}
            />
          )}

          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-sidebar/95">
              <div className="space-y-3 text-center">
                <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-sidebar-foreground/70">Cargando video...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-sidebar/95">
              <div className="max-w-sm space-y-3 px-4 text-center">
                <AlertTriangle className="mx-auto h-10 w-10 text-warning" />
                <p className="text-sm text-sidebar-foreground/70">{error}</p>
                <Button variant="outline" size="sm" onClick={handleClose}>
                  Cerrar
                </Button>
              </div>
            </div>
          )}

          {/* Close + Title overlay */}
          <div className="absolute left-4 right-4 top-4 z-20 flex items-start justify-between pointer-events-none">
            <h3 className="pointer-events-auto rounded-lg bg-sidebar/70 backdrop-blur-sm px-3 py-1.5 text-sm font-medium text-sidebar-foreground max-w-[75%] truncate">
              {title}
            </h3>
            <button
              onClick={handleClose}
              className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-sidebar/70 backdrop-blur-sm text-sidebar-foreground hover:bg-sidebar transition-colors"
              aria-label="Cerrar video"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {completed && (
            <div className="absolute bottom-4 left-4 z-20">
              <div
                className={cn(
                  'flex items-center gap-2 rounded-full bg-success px-3 py-1.5 text-sm text-success-foreground animate-scale-in',
                )}
              >
                <CheckCircle2 className="h-4 w-4" />
                Completado
              </div>
            </div>
          )}
        </div>
        </DialogContent>
    </Dialog>
  );
}
